// utils/offlineAuth.js
// Offline sign-in for factory tablets. The factory floor has no reliable wifi, so
// a photographer who signed in online once on this tablet must be able to sign in
// again without a connection.
//
// What is kept per user after a successful online login:
//   - a salted PBKDF2-SHA-256 check value of the password (never the password),
//   - the cached profile,
//   - the session token, encrypted with a key derived from the same password.
// The check value and the key are the two halves of one PBKDF2 output, so the
// stored half does not reveal the key. While a session is open the key sits next
// to the (plain) session token so the latest token can be sealed at sign-out;
// after sign-out only the password can open it again.

const RECORDS_KEY = 'offline_login_v1';
const ATTEMPTS_KEY = 'offline_login_attempts';
const SESSION_KEY = 'offline_login_session_key';

export const PBKDF2_ITERATIONS = 210000;
const MAX_RETRY_DELAY_MS = 5 * 60 * 1000;
// An offline record is only good for this long after the last online login on
// this device. Without a limit a password that was changed or an account that was
// deactivated would keep opening a tablet that never reaches the server.
export const OFFLINE_LOGIN_MAX_AGE_MS = 14 * 24 * 3600 * 1000;

export class OfflineLoginError extends Error {
  // code: 'unsupported' | 'unknown-user' | 'wrong-password' | 'locked' | 'expired'
  constructor(code, retryAfterMs = 0) {
    super(`offline login: ${code}`);
    this.name = 'OfflineLoginError';
    this.code = code;
    this.retryAfterMs = retryAfterMs;
  }
}

function subtle() {
  return (typeof crypto !== 'undefined' && crypto.subtle) || null;
}

export function offlineLoginSupported() {
  return !!subtle() && typeof TextEncoder !== 'undefined';
}

// Same normalisation the server applies at login.
function keyFor(username) {
  return String(username || '').trim().toLowerCase();
}

function toB64(bytes) {
  let s = '';
  bytes.forEach((b) => { s += String.fromCharCode(b); });
  return btoa(s);
}

function fromB64(b64) {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

function readJson(key) {
  try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; }
}

function writeJson(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// One PBKDF2 run yields the stored check value and the token key.
export async function deriveSecrets(password, salt, iterations = PBKDF2_ITERATIONS) {
  const material = await subtle().importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveBits']);
  const bits = new Uint8Array(await subtle().deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, material, 512));
  return { verifier: bits.slice(0, 32), key: bits.slice(32) };
}

async function encryptToken(rawKey, token) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await subtle().importKey('raw', rawKey, 'AES-GCM', false, ['encrypt']);
  const ct = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(token)));
  return { iv: toB64(iv), ct: toB64(ct) };
}

async function decryptToken(rawKey, sealed) {
  try {
    const key = await subtle().importKey('raw', rawKey, 'AES-GCM', false, ['decrypt']);
    const plain = await subtle().decrypt({ name: 'AES-GCM', iv: fromB64(sealed.iv) }, key, fromB64(sealed.ct));
    return new TextDecoder().decode(plain);
  } catch {
    return null;
  }
}

function sameBytes(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

export function hasOfflineLogin(username) {
  return !!readJson(RECORDS_KEY)[keyFor(username)];
}

// Called after a successful online login (or password change): from now on this
// user can sign in on this device without a connection.
export async function rememberOfflineLogin({ username, password, user, token }) {
  if (!offlineLoginSupported() || !keyFor(username) || !password) return false;
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const { verifier, key } = await deriveSecrets(password, salt);
  const records = readJson(RECORDS_KEY);
  records[keyFor(username)] = {
    v: 1,
    salt: toB64(salt),
    iterations: PBKDF2_ITERATIONS,
    verifier: toB64(verifier),
    user,
    token: token ? await encryptToken(key, token) : null,
    saved_at: new Date().toISOString(),
    // The last successful online login. Sealing a token at sign-out or updating
    // the profile never moves it.
    online_at: Date.now(),
  };
  writeJson(RECORDS_KEY, records);
  writeJson(SESSION_KEY, { username: keyFor(username), key: toB64(key) });
  clearAttempts(username);
  return true;
}

// Keep the cached profile current (role or factory changed on the server).
export function updateOfflineProfile(user) {
  const records = readJson(RECORDS_KEY);
  const record = records[keyFor(user?.username)];
  if (!record) return;
  record.user = user;
  writeJson(RECORDS_KEY, records);
}

// The server turned this user away (password changed, account deactivated): the
// old password must stop working on this device too.
export function forgetOfflineLogin(username) {
  const records = readJson(RECORDS_KEY);
  delete records[keyFor(username)];
  writeJson(RECORDS_KEY, records);
  clearAttempts(username);
  try { localStorage.removeItem(SESSION_KEY); } catch {}
}

// At sign-out: store the latest token under the user's key and drop the key, so
// a later offline sign-in resumes with a token the server can still renew.
let sealing = Promise.resolve();
export function sealOfflineSession(username, token) {
  const session = readJson(SESSION_KEY);
  try { localStorage.removeItem(SESSION_KEY); } catch {}
  if (!offlineLoginSupported() || !token || !session.key || session.username !== keyFor(username)) return sealing;
  sealing = sealing.then(async () => {
    const sealed = await encryptToken(fromB64(session.key), token);
    const records = readJson(RECORDS_KEY);
    const record = records[keyFor(username)];
    if (!record) return;
    record.token = sealed;
    writeJson(RECORDS_KEY, records);
  }).catch(() => {});
  return sealing;
}

// ── Wrong-password backoff ────────────────────────────────────────────────────

// 1s, 2s, 4s ... capped at 5 minutes.
export function offlineRetryDelayMs(failures) {
  if (failures <= 0) return 0;
  return Math.min(1000 * 2 ** (failures - 1), MAX_RETRY_DELAY_MS);
}

function clearAttempts(username) {
  const attempts = readJson(ATTEMPTS_KEY);
  delete attempts[keyFor(username)];
  writeJson(ATTEMPTS_KEY, attempts);
}

// A record without a readable stamp counts as expired: better one online login
// too many than a record that never ends.
function offlineRecordExpired(record) {
  const onlineAt = typeof record.online_at === 'number' ? record.online_at : Date.parse(record.saved_at);
  if (!Number.isFinite(onlineAt)) return true;
  return Date.now() - onlineAt > OFFLINE_LOGIN_MAX_AGE_MS;
}

// True when this is the password the device remembers for the user. Used after
// the server rejects a login: only then do we know the remembered password was
// changed. A typo matches nothing and must leave the record alone, or one slip
// on bad wifi would lock the user out of offline sign-in.
export async function matchesOfflinePassword(username, password) {
  try {
    if (!offlineLoginSupported() || !password) return false;
    const record = readJson(RECORDS_KEY)[keyFor(username)];
    if (!record) return false;
    const { verifier } = await deriveSecrets(password, fromB64(record.salt), record.iterations);
    return sameBytes(verifier, fromB64(record.verifier));
  } catch {
    return false;
  }
}

// Verify the password against the stored check value. Resolves with the cached
// profile and the last session token (null when it could not be recovered).
export async function offlineLogin(username, password) {
  if (!offlineLoginSupported()) throw new OfflineLoginError('unsupported');
  await sealing;
  const name = keyFor(username);
  const record = readJson(RECORDS_KEY)[name];
  if (!record) throw new OfflineLoginError('unknown-user');
  if (offlineRecordExpired(record)) throw new OfflineLoginError('expired');

  const attempts = readJson(ATTEMPTS_KEY);
  const state = attempts[name] || { failures: 0, next_at: 0 };
  const wait = state.next_at - Date.now();
  if (wait > 0) throw new OfflineLoginError('locked', wait);

  const { verifier, key } = await deriveSecrets(password, fromB64(record.salt), record.iterations);
  if (!sameBytes(verifier, fromB64(record.verifier))) {
    const failures = state.failures + 1;
    const delay = offlineRetryDelayMs(failures);
    attempts[name] = { failures, next_at: Date.now() + delay };
    writeJson(ATTEMPTS_KEY, attempts);
    throw new OfflineLoginError('wrong-password', delay);
  }

  clearAttempts(name);
  writeJson(SESSION_KEY, { username: name, key: toB64(key) });
  const token = record.token ? await decryptToken(key, record.token) : null;
  return { user: record.user, token };
}
